import React, { useState, useEffect, MouseEvent } from "react";
import { hasTOSChecked } from "../../auth/auth";
import Loading from "../common/Loading";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./termsandconditions.css";

// Seconds the user must spend on the page before the checkbox enables.
// Cross-origin iframes prevent detecting scroll position inside them,
// so a review timer is used as the standard alternative (similar to app
// stores and software installers).
export const TOS_REVIEW_SECONDS = 5;

type TermsAndConditionsProps = {
  onAgree: () => Promise<boolean>;
};

function TermsAndConditions({ onAgree }: TermsAndConditionsProps) {
  const [isAgreed, setIsAgreed] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [secondsRemaining, setSecondsRemaining] = useState<number>(TOS_REVIEW_SECONDS);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const canCheck = secondsRemaining <= 0;

  useEffect(() => {
    setIsLoading(true);
    const verifyTOS = async () => {
      const agreed = await hasTOSChecked();
      setIsAgreed(agreed);
      setIsLoading(false);
      if (agreed) {
        setIsSubmitting(true);
        try {
          await onAgree();
        } catch (error) {
          console.error("Failed to process Terms and Conditions agreement:", error);
        } finally {
          setIsSubmitting(false);
        }
      }
    };
    verifyTOS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown timer — starts once loading finishes
  useEffect(() => {
    if (isLoading) return;
    const interval = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isLoading]);

  const handleCheckboxClick = (e: MouseEvent<HTMLInputElement>) => {
    if (!canCheck) {
      e.preventDefault();
      toast.warn(
        `Please review the Terms of Service (${secondsRemaining}s remaining).`,
        { toastId: "review-warning" }
      );
    }
  };

  const handleCheckboxChange = () => {
    if (!canCheck) return;
    setIsAgreed((prev) => !prev);
  };

  const handleAgreeClick = async () => {
    setIsSubmitting(true);
    try {
      const success = await onAgree();
      if (!success) {
        toast.error(
          "We could not record your acceptance. Please try again.",
          { toastId: "tos-error" }
        );
      }
    } catch (error) {
      toast.error(
        "Something went wrong. Please check your connection and try again.",
        { toastId: "tos-error" }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="tos-page">
        <Loading />
      </div>
    );
  }

  return (
    <div className="tos-page">
      <div className="tos-card">
        <h2 className="tos-title">Terms of Service</h2>
        <p className="tos-subtitle">
          Please read our Terms of Service agreement carefully. Once you have
          reviewed the terms, check the box and click &quot;I Agree&quot; to
          continue.
        </p>

        <div className="tos-iframe-wrapper">
          <iframe
            src="https://www.computingreapplied.com/utcad"
            title="Terms and Conditions"
            className="tos-iframe"
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
          />
        </div>

        {!canCheck && (
          <p className="tos-timer-message">
            Please review the Terms of Service ({secondsRemaining}s remaining)
          </p>
        )}

        <div className="tos-actions">
          <label
            className={`tos-checkbox-label ${!canCheck ? "tos-checkbox-disabled" : ""}`}
          >
            <input
              type="checkbox"
              checked={isAgreed}
              onClick={handleCheckboxClick}
              onChange={handleCheckboxChange}
              className="tos-checkbox"
            />
            I certify that I am 18 years or older &amp; I agree to the Terms and
            Conditions
          </label>
          <button
            className="tos-agree-button"
            onClick={handleAgreeClick}
            disabled={!isAgreed || isSubmitting}
          >
            {isSubmitting ? "Processing..." : "I Agree"}
          </button>
        </div>
      </div>
      <ToastContainer position="top-center" />
    </div>
  );
}

export default TermsAndConditions;
